import { fetchWithAuth, getApiOrigin } from "@blpde/connect/auth";

/**
 * How the shell reaches a module's public API: every module is served
 * through master's gateway, routed by the header `BLP-Module`; master itself
 * answers without it. The request carries the session's bearer and UI
 * language and refreshes the token once on a 401 (`fetchWithAuth`) — the same
 * way the apps' own clients call their module.
 */

/** master answers without the module header; every other module is routed by it. */
export const MASTER_MODULE = "master";

/**
 * Calls `path` of a module's public API. `path` starts at the API's root and
 * names its version (`/v1/search/kinds`, `/v1/channels/5/…`).
 */
export function moduleApiRequest(
  module: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (module !== MASTER_MODULE) headers.set("BLP-Module", module);
  return fetchWithAuth(`${getApiOrigin()}${path}`, { ...init, headers });
}

/** A module key: lower snake case, as master checks it. */
const MODULE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** The methods a stored request may use (never a read: it changes something). */
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
/** master's cap on a stored request path. */
const MAX_MODULE_API_PATH = 512;

/** `path` without its query. */
function pathOnly(path: string): string {
  const at = path.indexOf("?");
  return at < 0 ? path : path.slice(0, at);
}

/**
 * Whether `path` is a plain path inside a module's public API — the rule
 * master applies before it stores a request a client is to perform
 * (`modulePath`, backend/master/notifications/actions.go), applied here once
 * more before such a request is sent: under `/v1/`, not in the gateway's
 * cross-module namespace (`/v1/modules/<uuid>/…` names its target itself),
 * no scheme or host, and nothing a proxy or router could read differently
 * than it is written — no dot segments, no doubled or encoded separators, no
 * encoded dots, no control characters.
 */
export function isModuleApiPath(path: unknown): path is string {
  if (typeof path !== "string") return false;
  if (!path.startsWith("/v1/") || path.length > MAX_MODULE_API_PATH) {
    return false;
  }
  // A scheme, a backslash or a control character has no place in a path.
  if (path.includes("://") || /[\\\x00-\x1f\x7f]/.test(path)) return false;
  const plain = pathOnly(path).toLowerCase();
  if (plain.startsWith("/v1/modules/") || plain.includes("//")) return false;
  if (["%2e", "%2f", "%5c", "%25"].some((bad) => plain.includes(bad))) {
    return false;
  }
  return !plain.split("/").some((segment) => segment === "." || segment === "..");
}

/** A request a notification stores for the client to perform, checked. */
export interface ModuleApiWrite {
  module: string;
  method: string;
  path: string;
}

/**
 * Whether a stored request may be sent at all: a module key, a writing
 * method and a plain path inside the module's API (`isModuleApiPath`).
 * Defence in depth — master refuses to store anything else.
 */
export function isModuleApiWrite(request: {
  module?: unknown;
  method?: unknown;
  path?: unknown;
}): request is ModuleApiWrite {
  return (
    typeof request.module === "string" &&
    MODULE_KEY_PATTERN.test(request.module) &&
    typeof request.method === "string" &&
    WRITE_METHODS.has(request.method.toUpperCase()) &&
    isModuleApiPath(request.path)
  );
}
